import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { FileText, CheckSquare, Square, Printer, X, Download, Search, Edit, Ban, RotateCcw, PlaneTakeoff, Package, Truck, ClipboardCheck, History } from "lucide-react";
import { format } from "date-fns";

import InvoiceHeader from "../components/invoice/InvoiceHeader";
import InvoiceTable from "../components/invoice/InvoiceTable";
import { InvoiceTotals, InvoiceBankSignature } from "../components/invoice/InvoiceFooter"; 

const InvoiceTemplate = ({ data, isCancelled }) => {
  if (!data || data.length === 0) return null;
  const header = data.find(d => d.additional_charges && d.additional_charges !== "[]") || data[0]; 
  const sortedData = [...data].sort((a, b) => new Date(a.date) - new Date(b.date));
  const MAX_ITEMS_PAGE_1 = 33;
  const MAX_ITEMS_PAGE_NEXT = 44;
  const SAFE_LIMIT_FOOTER = 10;
  const dynamicChunks = [];
  let dataIndex = 0;
  let isFirstPage = true;

  while (dataIndex < sortedData.length) {
    const maxAllowed = isFirstPage ? MAX_ITEMS_PAGE_1 : MAX_ITEMS_PAGE_NEXT;
    const remaining = sortedData.length - dataIndex;
    if (remaining <= maxAllowed) {
      if (remaining > (maxAllowed - SAFE_LIMIT_FOOTER)) {
        dynamicChunks.push(sortedData.slice(dataIndex, dataIndex + remaining));
        dynamicChunks.push([]); 
      } else {
        dynamicChunks.push(sortedData.slice(dataIndex, dataIndex + remaining));
      }
      break;
    } else {
      dynamicChunks.push(sortedData.slice(dataIndex, dataIndex + maxAllowed));
      dataIndex += maxAllowed;
    }
    isFirstPage = false;
  }

  const totalPages = dynamicChunks.length;

  return (
    <div className="invoice-print-wrapper" style={{ fontFamily: 'Arial, Helvetica, sans-serif' }}>
      {dynamicChunks.map((chunk, index) => {
        const pageNum = index + 1;
        const isLastPage = pageNum === totalPages;
        const headerWithPage = { ...header, page_info: `PAGE ${pageNum} OF ${totalPages}` };
        let rollingStartIndex = 0;
        for(let i = 0; i < index; i++) rollingStartIndex += dynamicChunks[i].length;

        return (
          <div key={pageNum} className="print-page bg-white flex flex-col w-full box-border relative" style={{ height: '296mm', padding: '10mm 15mm' }}>
              {isCancelled && (
                 <div className="absolute inset-0 flex items-center justify-center opacity-10 pointer-events-none z-10">
                     <span className="text-8xl font-black text-red-600 border-8 border-red-600 p-8 transform -rotate-45 tracking-widest">CANCELLED</span>
                 </div>
              )}
              <div className="relative z-20 flex flex-col h-full">
                  <InvoiceHeader header={headerWithPage} />
                  {chunk.length > 0 ? (
                      <div className="mt-4">
                          <InvoiceTable data={chunk} startIndex={rollingStartIndex} />
                          {isLastPage && <div className="mt-2"><InvoiceTotals data={sortedData} header={header} /></div>}
                          {!isLastPage && <div className="text-center text-xs font-bold text-gray-400 italic mt-4 border-t border-dashed pt-2">--- Continued on next page ---</div>}
                      </div>
                  ) : (
                      <div className="mt-4">
                         <div className="font-bold text-lg border-b-2 border-black pb-1 mb-4 italic text-gray-500">Invoice Continuation Summary</div>
                         <InvoiceTotals data={sortedData} header={header} />
                      </div>
                  )}
                  {isLastPage && <div className="mt-auto pb-4"><InvoiceBankSignature /></div>}
              </div>
          </div>
        );
      })}
    </div>
  );
};

const InvoiceHistoryPage = () => {
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = currentUser.role === 'ADMIN';
  const userPerms = (() => { try { return JSON.parse(currentUser.permissions || '[]'); } catch(e) { return []; } })();
  const canEdit   = isAdmin || currentUser.role === 'SUPER_USER' || userPerms.includes('Invoice C - Edit');
  const canCancel = isAdmin || userPerms.includes('Invoice C - Cancel');
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [selectedInvoiceNumbers, setSelectedInvoiceNumbers] = useState([]); 
  const [activeTab, setActiveTab] = useState("EXPORT"); 
  const [searchDateFrom, setSearchDateFrom] = useState("");
  const [searchDateTo, setSearchDateTo] = useState("");
  const [searchInv, setSearchInv] = useState("");
  const [searchCust, setSearchCust] = useState("");
  const [searchCustId, setSearchCustId] = useState("");
  const [searchAwb, setSearchAwb] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [bulkPrintData, setBulkPrintData] = useState([]); 
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editForm, setEditForm] = useState({ id: '', awb: '', vat_percent: 1.1, fsc_percent: 0, vat_amount: "", invoice_date: '' });
  const [addCharges, setAddCharges] = useState([]);
  const [deductions, setDeductions] = useState([]);
  const [isCash, setIsCash] = useState(false);
  const [editModalTab, setEditModalTab] = useState("pajak");
  const [custForm, setCustForm] = useState({ customer_name: '', customer_id: '', department: '', building: '', address1: '', address2: '', city: '', post_code: '', npwp: '' });
  const [editInvoiceNumber, setEditInvoiceNumber] = useState('');
  const [currentRevision, setCurrentRevision] = useState(0);
  const [isSplitModalOpen, setIsSplitModalOpen] = useState(false);
  const [isRevisionModalOpen, setIsRevisionModalOpen] = useState(false);
  const [revisionHistory, setRevisionHistory] = useState([]);
  const [revisionInvoiceNumber, setRevisionInvoiceNumber] = useState('');
  const [loadingRevisions, setLoadingRevisions] = useState(false);
  const [splitInvoiceNumber, setSplitInvoiceNumber] = useState('');
  const [splitInvoiceItems, setSplitInvoiceItems] = useState([]);
  const [splitSelectedIds, setSplitSelectedIds] = useState([]);
  const [splitDate, setSplitDate] = useState('');
  const [isSplitting, setIsSplitting] = useState(false);

  const formatInputDisplay = (val) => {
    if (val === "" || val === null || val === undefined) return "";
    return val.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  };

  const parseRawAmount = (val) => {
    const clean = val.replace(/\./g, "");
    return clean === "" ? 0 : parseFloat(clean);
  };

  const API_URL = "/api";

  useEffect(() => { refreshData(1, activeTab); }, []);
  useEffect(() => { setCurrentPage(1); refreshData(1, activeTab); }, [searchDateFrom, searchDateTo]);

  const refreshData = async (page = 1, cat = null) => {
    setIsLoading(true);
    try {
        const params = new URLSearchParams({ page, limit: itemsPerPage });
        const search = searchInv || searchAwb || searchCust || searchCustId;
        if (search) params.append('search', search);
        params.append('category', cat || activeTab);
        if (searchDateFrom) params.append('date_from', searchDateFrom);
        if (searchDateTo) params.append('date_to', searchDateTo);
        const res = await axios.get(`${API_URL}/invoices/history/international?${params}`);
        setData(res.data.data || []);
        setTotalItems(res.data.total || 0);
        setTotalPages(res.data.total_pages || 1);
        setSelectedInvoiceNumbers([]);
    } catch (error) { console.error(error); }
    finally { setIsLoading(false); }
  };

  const handleTabChange = (tabName) => {
      setActiveTab(tabName);
      setSelectedInvoiceNumbers([]);
      setCurrentPage(1);
      refreshData(1, tabName);
  };

  const resetSearch = () => {
      setSearchDateFrom(""); setSearchDateTo(""); setSearchInv("");
      setSearchCust(""); setSearchCustId(""); setSearchAwb("");
      setCurrentPage(1);
  };

  // Filter tanggal sudah diterapkan di server (lihat refreshData), data sini sudah sesuai rentang.
  const filteredData = data;
  const currentTableData = filteredData;
  

  const totalPagesUI = totalPages;

  const handleCancelInvoice = async (invNumber) => {
      if(!window.confirm(`Yakin membatalkan Invoice ${invNumber}?`)) return;
      const loadToast = toast.loading("Membatalkan invoice...");
      try {
          await axios.put(`${API_URL}/invoices/cancel/${invNumber}`, {}, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
          toast.success("Invoice DIBATALKAN!");
          refreshData();
      } catch (error) { toast.error("Gagal membatalkan invoice."); } 
      finally { toast.dismiss(loadToast); }
  };

  const handleRestoreInvoice = async (invNumber) => {
      if(!window.confirm(`Yakin mengaktifkan kembali Invoice ${invNumber}?`)) return;
      const loadToast = toast.loading("Mengaktifkan invoice...");
      try {
          await axios.put(`${API_URL}/invoices/restore/${invNumber}`, {}, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
          toast.success("Invoice kembali AKTIF!");
          refreshData();
      } catch (error) { toast.error("Gagal mengaktifkan invoice."); } 
      finally { toast.dismiss(loadToast); }
  };

  const handleViewRevisions = async (invoiceNumber) => {
    setRevisionInvoiceNumber(invoiceNumber);
    setIsRevisionModalOpen(true);
    setLoadingRevisions(true);
    try {
      const res = await axios.get(`${API_URL}/invoices/${invoiceNumber}/revisions`);
      setRevisionHistory(res.data.revisions || []);
    } catch (error) {
      toast.error("Gagal load history revisi");
    } finally {
      setLoadingRevisions(false);
    }
  };

  const handleOpenSplit = (item) => {
    const invItems = data.filter(d => d.invoice_number === item.invoice_number && d.revision_status === 'ACTIVE');
    setSplitInvoiceNumber(item.invoice_number);
    setSplitInvoiceItems(invItems);
    setSplitSelectedIds([]);
    setSplitDate('');
    setIsSplitModalOpen(true);
  };

  const handleSplit = async () => {
    if (splitSelectedIds.length === 0) { toast.error("Pilih minimal 1 AWB"); return; }
    if (!splitDate) { toast.error("Pilih tanggal invoice baru"); return; }
    if (splitSelectedIds.length === splitInvoiceItems.length) { toast.error("Minimal 1 AWB harus tersisa di invoice asal"); return; }
    const loadToast = toast.loading("Memproses split invoice...");
    try {
      const res = await axios.post(`${API_URL}/invoices/${splitInvoiceNumber}/split`, {
        awb_ids: splitSelectedIds,
        invoice_date: splitDate
      }, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      toast.success(`Split berhasil! ${res.data.moved_count} AWB pindah ke ${res.data.new_invoice_number}`, { id: loadToast });
      setIsSplitModalOpen(false);
      refreshData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Gagal split invoice", { id: loadToast });
    }
  };

  const handleEditInvoice = (item) => {
      setEditModalTab("pajak");
      setEditInvoiceNumber(item.invoice_number || '');
      setCurrentRevision(item.revision || 0);
      setCustForm({
          customer_name: item.customer_name || '',
          customer_id: item.customer_id || '',
          department: item.department || '',
          building: item.customer_building || '',
          address1: item.customer_address1 || '',
          address2: item.customer_address2 || '',
          city: item.customer_city || '',
          post_code: item.customer_post_code || '',
          npwp: item.customer_npwp || '',
      });
      const invoiceItems = data.filter(d => d.invoice_number === item.invoice_number);
      const totalBasePrice = invoiceItems.reduce((sum, d) => sum + (d.base_price || 0), 0);
      const fscPercent = parseFloat(item.fsc_percent) || 0;
      const vatPercent = parseFloat(item.vat_percent) || 1.1;
      const fscTotal = Math.round(totalBasePrice * (fscPercent / 100));
      let totalAdditional = 0;
      try {
          const addList = JSON.parse(item.additional_charges || "[]");
          totalAdditional = addList.reduce((sum, a) => sum + (a.amount || 0), 0);
      } catch(e) {}
      const dpp = totalBasePrice + fscTotal + totalAdditional;
      const autoVat = Math.round(dpp * (vatPercent / 100));
      setEditForm({ id: item.id, awb: item.awb, vat_percent: vatPercent, fsc_percent: fscPercent, vat_amount: item.vat_amount > 0 ? item.vat_amount : autoVat, invoice_date: item.invoice_date ? item.invoice_date.substring(0, 10) : '' });
      try {
          setAddCharges(JSON.parse(item.additional_charges || "[]"));
          setDeductions(JSON.parse(item.deductions || "[]"));
      } catch (e) { setAddCharges([]); setDeductions([]); }
      const invDate = item.invoice_date ? item.invoice_date.substring(0, 10) : "";
      const dueDate = item.due_date ? item.due_date.substring(0, 10) : "";
      setIsCash(invDate !== "" && invDate === dueDate);
      setIsEditModalOpen(true);
  };
  
  const handleRollbackRevision = async (invNumber, revision) => {
      if (!window.confirm(`Yakin rollback ke Rev.${revision - 1}? Rev.${revision} akan dihapus.`)) return;
      const loadToast = toast.loading("Rollback revisi...");
      try {
          await axios.delete(`${API_URL}/invoices/${invNumber}/revision/${revision}`, {
              headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          });
          toast.success("Rollback berhasil!", { id: loadToast });
          refreshData();
      } catch (error) {
          toast.error(error.response?.data?.detail || "Gagal rollback", { id: loadToast });
      }
  };

  const submitRevision = async () => {
      if (!window.confirm('Yakin membuat revisi? Invoice lama akan menjadi SUPERSEDED.')) return;
      const loadToast = toast.loading("Membuat revisi...");
      try {
          await axios.post(`${API_URL}/invoices/${editInvoiceNumber}/revise`, custForm, {
              headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          });
          toast.success("Revisi berhasil dibuat!", { id: loadToast });
          setIsEditModalOpen(false);
          refreshData();
      } catch (error) {
          toast.error(error.response?.data?.detail || "Gagal membuat revisi", { id: loadToast });
      }
  };

  const submitEdit = async () => {
      const loadToast = toast.loading("Menyimpan perubahan...");
      try {
          await axios.put(`${API_URL}/invoices/${editForm.id}`, {
              vat_percent: parseFloat(editForm.vat_percent) || 0,
              fsc_percent: parseFloat(editForm.fsc_percent) || 0,
              vat_amount: editForm.vat_amount === "" || editForm.vat_amount === 0 ? 0 : parseFloat(editForm.vat_amount) || 0,
              additional_charges: JSON.stringify(addCharges),
              deductions: JSON.stringify(deductions),
              is_cash: isCash,
              invoice_date: editForm.invoice_date || null
          }, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
          toast.success("Berhasil disimpan!");
          setIsEditModalOpen(false);
          refreshData(); 
      } catch (error) { toast.error("Gagal simpan!"); } 
      finally { toast.dismiss(loadToast); }
  };

  const toggleSelectHistory = (invNumber) => {
      setSelectedInvoiceNumbers(prev => 
          prev.includes(invNumber) ? prev.filter(n => n !== invNumber) : [...prev, invNumber]
      );
  };

  const handleSelectAllHistory = async () => {
      // Ambil SEMUA invoice yang cocok filter (semua halaman), bukan cuma 50 di layar
      const loadingToast = toast.loading("Mengambil semua data...");
      try {
          const params = new URLSearchParams({ page: 1, limit: 100000 });
          const search = searchInv || searchAwb || searchCust || searchCustId;
          if (search) params.append('search', search);
          params.append('category', activeTab);
          if (searchDateFrom) params.append('date_from', searchDateFrom);
          if (searchDateTo) params.append('date_to', searchDateTo);
          const res = await axios.get(`${API_URL}/invoices/history/international?${params}`);
          let all = res.data.data || [];
          const uniqueAll = [...new Set(all.map(d => d.invoice_number).filter(Boolean))];
          if (selectedInvoiceNumbers.length === uniqueAll.length && uniqueAll.length > 0) {
              setSelectedInvoiceNumbers([]);
              toast.success("Pilihan dibatalkan", { id: loadingToast });
          } else {
              setSelectedInvoiceNumbers(uniqueAll);
              toast.success(`${uniqueAll.length} invoice terpilih (semua halaman)`, { id: loadingToast });
          }
      } catch (e) {
          console.error(e);
          toast.error("Gagal mengambil semua data", { id: loadingToast });
      }
  };

  const handleDownloadExcel = async () => {
    let rawItemsToDownload = selectedInvoiceNumbers.length > 0
        ? data.filter(d => selectedInvoiceNumbers.includes(d.invoice_number))
        : filteredData;
    if (rawItemsToDownload.length === 0) return toast.error("Tidak ada data untuk didownload");
    const idsToDownload = rawItemsToDownload.map(item => item.id);
    const loadingToast = toast.loading("Membentuk file Excel...");
    try {
      const res = await axios.post(`${API_URL}/invoices/history/export`, { ids: idsToDownload }, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `History_Invoice_${activeTab}_${format(new Date(), 'ddMMyyyy')}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Excel berhasil didownload!", { id: loadingToast });
    } catch (error) { toast.error("Gagal mendownload Excel", { id: loadingToast }); }
  };



  const handleOpenPreview = async (invNumber) => {
    const loadingToast = toast.loading("Memuat data invoice...");
    try {
        const res = await axios.post(`${API_URL}/invoices/by-numbers`, { invoice_numbers: [invNumber] });
        const items = res.data;
        if(items.length === 0) return toast.error("Data tidak ditemukan!");
        setBulkPrintData([{ items, isCancelled: items[0].status === 'CANCELLED' }]);
        setShowPreview(true);
        toast.dismiss(loadingToast);
    } catch (error) { toast.error("Gagal memuat data", { id: loadingToast }); }
  };

  const handleBulkPrint = async () => {
    if (selectedInvoiceNumbers.length === 0) return toast.error("Pilih minimal 1 Invoice!");
    const loadingToast = toast.loading("Memuat data invoice...");
    try {
        const res = await axios.post(`${API_URL}/invoices/by-numbers`, { invoice_numbers: selectedInvoiceNumbers });
        const allItems = res.data;
        const grouped = [];
        selectedInvoiceNumbers.forEach(invNum => {
            const items = allItems.filter(d => d.invoice_number === invNum);
            if (items.length > 0) grouped.push({ items, isCancelled: items[0].status === 'CANCELLED' });
        });
        if (grouped.length === 0) return toast.error("Data tidak ditemukan!");
        setBulkPrintData(grouped);
        setShowPreview(true);
        toast.dismiss(loadingToast);
    } catch (error) { toast.error("Gagal memuat data", { id: loadingToast }); }
  };

  const handleOpenReceiptPreview = async () => {
    if (selectedInvoiceNumbers.length === 0) return toast.error("Pilih minimal 1 Invoice untuk dibuat Tanda Terima!");
    const loadingToast = toast.loading("Memuat semua invoice terpilih...");
    try {
      const res = await axios.post(`${API_URL}/invoices/by-numbers`, {
        invoice_numbers: selectedInvoiceNumbers,
      });
      const firstByNumber = new Map();
      (res.data || []).forEach(item => {
        if (item.invoice_number && !firstByNumber.has(item.invoice_number)) {
          firstByNumber.set(item.invoice_number, item);
        }
      });
      const missing = selectedInvoiceNumbers.filter(number => !firstByNumber.has(number));
      if (missing.length > 0) {
        toast.error(`${missing.length} invoice terpilih tidak ditemukan. Muat ulang History lalu pilih kembali.`, { id: loadingToast });
        return;
      }
      const receipts = selectedInvoiceNumbers.map(number => firstByNumber.get(number));
      toast.dismiss(loadingToast);
      navigate('/receipt', { state: { invoices: receipts } });
    } catch (error) {
      toast.error("Gagal memuat data Tanda Terima", { id: loadingToast });
    }
  };

  const handleNativePrint = () => {
    const printWindow = window.open("", "_blank");
    const printContent = document.getElementById("printable-area").innerHTML;
    const styles = Array.from(document.styleSheets).map(sheet => {
        try { return Array.from(sheet.cssRules).map(rule => rule.cssText).join("\n"); }
        catch(e) { return ""; }
    }).join("\n");
    printWindow.document.write(`
      <html><head><title>Invoice</title>
      <style>
        ${styles}
        @page { size: A4 portrait; margin: 0; }
        body { margin: 0; padding: 0; background: white; }
        .print-page { width: 210mm; min-height: 296mm; max-height: 296mm; page-break-after: always; page-break-inside: avoid; display: block; margin: 0; padding: 10mm 15mm; box-sizing: border-box; overflow: hidden; }
        * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      </style>
      </head><body>${printContent}</body></html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => { printWindow.print(); printWindow.close(); }, 800);
  };

  const handleDownloadPDFServer = async () => {
    if (bulkPrintData.length === 0) return;
    const allIds = bulkPrintData.flatMap(group => group.items.map(i => i.id));
    const fileName = bulkPrintData.length === 1
      ? `Invoice_${bulkPrintData[0].items[0]?.invoice_number || 'Draft'}.pdf`
      : `Bulk_Invoices_${bulkPrintData.length}_PT.pdf`;
    const loadToast = toast.loading(`Membuat PDF... (${bulkPrintData.length} invoice)`);
    setIsGeneratingPDF(true);
    try {
      const res = await axios.post(`${API_URL}/invoices/history/export-pdf`, { ids: allIds }, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`Berhasil download: ${fileName}`, { id: loadToast });
    } catch (error) {
      toast.error("Gagal download PDF", { id: loadToast });
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const hasActiveSearch = searchDateFrom || searchDateTo || searchInv || searchCust || searchCustId || searchAwb;

  return (
    <>
      <div className="print:hidden">
          <div className="max-w-7xl mx-auto p-3 sm:p-6 space-y-4 sm:space-y-6">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                <div>
                    <h1 className="text-2xl font-bold flex items-center gap-2 text-gray-900">
                        <FileText className="text-green-600"/> Invoice History
                    </h1>
                    <p className="text-sm text-gray-500 mt-1">Cari, Edit, Batalkan, dan Cetak Invoice yang sudah dirilis.</p>
                </div>
                <div className="flex flex-wrap gap-2">
                    <button onClick={handleDownloadExcel} className="bg-white text-green-700 border-2 border-green-600 px-3 py-2 rounded-lg font-bold hover:bg-green-50 flex items-center gap-2 transition shadow-sm">
                        <Download size={16}/> <span className="hidden sm:inline">Tarik Excel</span>
                    </button>

                    {selectedInvoiceNumbers.length > 0 && (
                        <div className="flex gap-2 bg-green-50 p-2 rounded-xl border border-green-200 animate-in fade-in slide-in-from-right-4 shadow-sm">
                            <div className="px-3 flex flex-col justify-center border-r border-green-200 mr-1">
                                <span className="text-xs text-green-600 font-bold">Terpilih:</span>
                                <span className="text-lg font-black text-green-800 leading-none">{selectedInvoiceNumbers.length} PT</span>
                            </div>
                            <button onClick={handleOpenReceiptPreview} className="bg-purple-600 text-white px-4 py-2 rounded-lg font-bold hover:bg-purple-700 flex items-center gap-2 shadow-sm transition mr-2">
                                <ClipboardCheck size={16}/> Tanda Terima
                            </button>
                            <div className="w-px bg-green-200 mx-1 h-full"></div>
                            <button onClick={handleBulkPrint} className="bg-green-600 text-white px-4 py-2 rounded-lg font-bold hover:bg-green-700 flex items-center gap-2 shadow-sm transition">
                                <Printer size={16}/> Cetak Massal (Print/PDF)
                            </button>
                        </div>
                    )}
                </div>
            </div>

            <div className="bg-gray-100 p-1.5 rounded-xl inline-flex gap-1 shadow-inner border border-gray-200/50">
                <button onClick={() => handleTabChange("EXPORT")} className={`px-3 sm:px-6 py-2.5 rounded-lg flex items-center gap-1 sm:gap-2 text-sm font-bold transition-all duration-300 ${activeTab === 'EXPORT' ? 'bg-white text-blue-700 shadow-md transform scale-105' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200'}`}>
                    <PlaneTakeoff size={16} /> <span className="hidden xs:inline">Export</span><span className="hidden sm:inline"> (C)</span>
                </button>
                <button onClick={() => handleTabChange("IMPORT")} className={`px-3 sm:px-6 py-2.5 rounded-lg flex items-center gap-1 sm:gap-2 text-sm font-bold transition-all duration-300 ${activeTab === 'IMPORT' ? 'bg-white text-orange-600 shadow-md transform scale-105' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200'}`}>
                    <Package size={16} /> <span className="hidden xs:inline">Import</span><span className="hidden sm:inline"> / GIMP (K)</span>
                </button>
                <button onClick={() => handleTabChange("DOMESTIC")} className={`px-3 sm:px-6 py-2.5 rounded-lg flex items-center gap-1 sm:gap-2 text-sm font-bold transition-all duration-300 ${activeTab === 'DOMESTIC' ? 'bg-white text-purple-600 shadow-md transform scale-105' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200'}`}>
                    <Truck size={16} /> <span className="hidden xs:inline">Domestic</span><span className="hidden sm:inline"> (D)</span>
                </button>
            </div>

            <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-200 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                    <div>
                        <label className="text-xs font-bold text-gray-500 uppercase">Tanggal Dari</label>
                        <input type="date" className="w-full mt-1 p-2.5 border rounded-lg text-sm bg-gray-50 outline-none focus:border-green-500 focus:bg-white transition" value={searchDateFrom} onChange={e => { setSearchDateFrom(e.target.value); setCurrentPage(1); }} />
                    </div>
                    <div>
                        <label className="text-xs font-bold text-gray-500 uppercase">Tanggal Sampai</label>
                        <input type="date" className="w-full mt-1 p-2.5 border rounded-lg text-sm bg-gray-50 outline-none focus:border-green-500 focus:bg-white transition" value={searchDateTo} onChange={e => { setSearchDateTo(e.target.value); setCurrentPage(1); }} />
                    </div>
                    <div>
                        <label className="text-xs font-bold text-gray-500 uppercase">Search Invoice No</label>
                        <div className="relative">
                            <Search className="absolute left-3 top-3 text-gray-400" size={16} />
                            <input type="text" placeholder={`${activeTab === 'EXPORT' ? 'C' : activeTab === 'IMPORT' ? 'K' : 'D'}-260...`} className="w-full mt-1 p-2.5 pl-9 border rounded-lg text-sm bg-gray-50 outline-none focus:border-green-500 focus:bg-white transition" value={searchInv} onChange={e => { setSearchInv(e.target.value); setCurrentPage(1); refreshData(1); }} />
                        </div>
                    </div>
                    <div>
                        {hasActiveSearch ? (
                            <button onClick={resetSearch} className="w-full mt-1 p-2.5 border-2 border-red-200 text-red-600 bg-red-50 hover:bg-red-100 rounded-lg text-sm font-bold transition flex items-center justify-center gap-2">
                                <X size={14}/> Reset Filter
                            </button>
                        ) : <div />}
                    </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                        <label className="text-xs font-bold text-gray-500 uppercase">Search Customer</label>
                        <div className="relative">
                            <Search className="absolute left-3 top-3 text-gray-400" size={16} />
                            <input type="text" placeholder="Nama PT..." className="w-full mt-1 p-2.5 pl-9 border rounded-lg text-sm bg-gray-50 outline-none focus:border-green-500 focus:bg-white transition" value={searchCust} onChange={e => { setSearchCust(e.target.value); setCurrentPage(1); refreshData(1); }} />
                        </div>
                    </div>
                    <div>
                        <label className="text-xs font-bold text-gray-500 uppercase">Search Customer ID</label>
                        <div className="relative">
                            <Search className="absolute left-3 top-3 text-gray-400" size={16} />
                            <input type="text" placeholder="J0018, CD001..." className="w-full mt-1 p-2.5 pl-9 border rounded-lg text-sm bg-gray-50 outline-none focus:border-green-500 focus:bg-white transition" value={searchCustId} onChange={e => { setSearchCustId(e.target.value); setCurrentPage(1); refreshData(1); }} />
                        </div>
                    </div>
                    <div>
                        <label className="text-xs font-bold text-gray-500 uppercase">Search AWB</label>
                        <div className="relative">
                            <Search className="absolute left-3 top-3 text-gray-400" size={16} />
                            <input type="text" placeholder="No Resi..." className="w-full mt-1 p-2.5 pl-9 border rounded-lg text-sm bg-gray-50 outline-none focus:border-green-500 focus:bg-white transition" value={searchAwb} onChange={e => { setSearchAwb(e.target.value); setCurrentPage(1); refreshData(1); }} />
                        </div>
                    </div>
                </div>
                {hasActiveSearch && (
                    <div className="text-xs text-green-700 bg-green-50 px-3 py-2 rounded-lg border border-green-200 font-medium">
                        Menampilkan <strong>{filteredData.length}</strong> hasil yang sesuai filter
                    </div>
                )}
            </div>

            <div className="bg-white border rounded-xl shadow-sm overflow-hidden relative z-0">
                <div className="overflow-x-auto">
                <table className="w-full text-left text-sm whitespace-nowrap min-w-[700px]">
                    <thead className="bg-gray-50 border-b uppercase text-xs text-gray-500">
                        <tr>
                            <th className="px-4 py-4 w-10 text-center border-r">
                                <button onClick={handleSelectAllHistory} className="hover:text-green-600 transition-colors">
                                    {selectedInvoiceNumbers.length > 0 && [...new Set(filteredData.map(d=>d.invoice_number).filter(Boolean))].every(n => selectedInvoiceNumbers.includes(n)) ? <CheckSquare className="text-green-600 mx-auto" size={20}/> : <Square className="text-gray-400 mx-auto" size={20}/>}
                                </button>
                            </th>
                            <th className="px-4 py-4 font-bold">AWB / Inv No</th>
                            <th className="px-4 py-4 font-bold">Customer</th>
                            <th className="px-4 py-4 font-bold text-right">Weight</th>
                            <th className="px-4 py-4 font-bold text-right">Total Amount</th>
                            <th className="px-4 py-4 font-bold text-center">Status</th>
                            <th className="px-4 py-4 font-bold text-center">Action</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {currentTableData.length === 0 ? (
                            <tr><td colSpan="7" className="p-16 text-center text-gray-400 font-medium">Belum ada data {activeTab} yang ditemukan.</td></tr>
                        ) : (
                            currentTableData.map(item => {
                                const isSelected = selectedInvoiceNumbers.includes(item.invoice_number);
                                const isCancelled = item.status === 'CANCELLED';
                                return (
                                    <tr key={item.id} className={`hover:bg-gray-50 transition-colors ${isSelected ? 'bg-green-50/50' : ''} ${isCancelled ? 'opacity-60 bg-red-50/30' : ''}`}>
                                        <td className="px-4 py-3 text-center border-r">
                                            <button onClick={() => toggleSelectHistory(item.invoice_number)}>
                                                {isSelected ? <CheckSquare className="text-green-600 mx-auto" size={18}/> : <Square className="text-gray-300 mx-auto" size={18}/>}
                                            </button>
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className={`font-bold ${isCancelled ? 'text-red-700 line-through' : 'text-gray-900'}`}>
                                                <span className={`${activeTab === 'EXPORT' ? 'text-blue-600' : activeTab === 'IMPORT' ? 'text-orange-600' : 'text-purple-600'}`}>{item.invoice_number?.substring(0, 2)}</span>
                                                {item.invoice_number?.substring(2)}
                                            </div>
                                            <div className="text-xs text-gray-500 mt-0.5"><span className="font-mono bg-gray-100 px-1 rounded mr-1">AWB</span> {item.awb}</div>
                                            <div className="text-[10px] text-gray-400 mt-1">{new Date(item.date).toLocaleDateString()}</div>
                                            {(item.revision > 0) && <div className="text-[10px] text-orange-500 font-bold mt-0.5">Rev.{item.revision}</div>}
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="font-bold text-gray-800 truncate w-48">{item.customer_name}</div>
                                            <div className="text-xs text-gray-500 font-mono mt-0.5">{item.customer_id}</div>
                                        </td>
                                        <td className="px-4 py-3 text-right text-gray-700">{item.weight_gram ? (item.weight_gram / 1000) : item.weight_kg} Kg</td>
                                        <td className="px-4 py-3 text-right font-mono font-bold text-gray-900">Rp {(item.total_amount || 0).toLocaleString('id-ID')}</td>
                                        <td className="px-4 py-3 text-center">
                                            {isCancelled ? (
                                                <span className="bg-red-100 text-red-700 text-[10px] px-2 py-1 rounded font-bold uppercase tracking-wider border border-red-200">Cancelled</span>
                                            ) : item.revision_status === 'SUPERSEDED' ? (
                                                <span className="bg-gray-100 text-gray-500 text-[10px] px-2 py-1 rounded font-bold uppercase tracking-wider border border-gray-200">Superseded</span>
                                            ) : (
                                                <span className="bg-green-100 text-green-700 text-[10px] px-2 py-1 rounded font-bold uppercase tracking-wider border border-green-200">Active</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex justify-center gap-1">
                                                {canEdit && item.status !== 'CANCELLED' && item.revision_status !== 'SUPERSEDED' && (
                                                    <button onClick={() => handleEditInvoice(item)} className="text-gray-400 hover:text-yellow-600 hover:bg-yellow-50 p-2 rounded-lg transition" title="Edit Invoice">
                                                        <Edit size={18} />
                                                    </button>
                                                )}
                                                {item.status !== 'CANCELLED' && item.revision_status !== 'SUPERSEDED' && (
                                                    <button onClick={() => handleOpenSplit(item)} className="text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 p-2 rounded-lg transition" title="Split Invoice">
                                                        <FileText size={18} />
                                                    </button>
                                                )}
                                                <button onClick={() => handleOpenPreview(item.invoice_number)} className="text-gray-400 hover:text-blue-600 hover:bg-blue-50 p-2 rounded-lg transition" title="Preview & Download">
                                                    <Printer size={18} />
                                                </button>
                                                {isCancelled ? (
                                                    canCancel && <button onClick={() => handleRestoreInvoice(item.invoice_number)} className="text-gray-400 hover:text-green-600 hover:bg-green-50 p-2 rounded-lg transition" title="Restore"><RotateCcw size={18} /></button>
                                                ) : item.revision_status !== 'SUPERSEDED' ? (
                                                    canCancel && <button onClick={() => handleCancelInvoice(item.invoice_number)} className="text-gray-400 hover:text-red-600 hover:bg-red-50 p-2 rounded-lg transition" title="Cancel"><Ban size={18} /></button>
                                                ) : null}
                                                {item.revision > 0 && (
                                                    <button onClick={() => handleViewRevisions(item.invoice_number)} className="text-gray-400 hover:text-blue-600 hover:bg-blue-50 p-2 rounded-lg transition" title="Lihat History Revisi">
                                                        <History size={18} />
                                                    </button>
                                                )}
                                                {isAdmin && item.revision > 0 && item.revision_status === 'ACTIVE' && (
                                                    <button onClick={() => handleRollbackRevision(item.invoice_number, item.revision)} className="text-gray-400 hover:text-purple-600 hover:bg-purple-50 p-2 rounded-lg transition" title={`Rollback ke Rev.${item.revision - 1}`}><RotateCcw size={18} /></button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                )
                            })
                        )}
                    </tbody>
                </table>
                </div>
                {totalPagesUI > 1 && (
                    <div className="bg-white p-4 flex justify-between items-center border-t">
                        <span className="text-sm text-gray-500 font-medium">Showing <b className="text-gray-900">{currentTableData.length}</b> of <b className="text-gray-900">{filteredData.length}</b> items</span>
                        <div className="flex gap-2">
                            <button onClick={() => { const p = Math.max(currentPage-1,1); setCurrentPage(p); refreshData(p, activeTab); }} disabled={currentPage === 1} className="px-4 py-2 bg-gray-50 border rounded-lg text-sm font-bold text-gray-600 disabled:opacity-40 hover:bg-gray-100 transition">Prev</button>
                            <div className="px-4 py-2 bg-green-50 text-green-700 border border-green-100 rounded-lg text-sm font-bold">Page {currentPage} of {totalPagesUI} ({totalItems} invoice)</div>
                            <button onClick={() => { const p = Math.min(currentPage+1,totalPagesUI); setCurrentPage(p); refreshData(p, activeTab); }} disabled={currentPage === totalPagesUI} className="px-4 py-2 bg-gray-50 border rounded-lg text-sm font-bold text-gray-600 disabled:opacity-40 hover:bg-gray-100 transition">Next</button>
                        </div>
                    </div>
                )}
            </div>
          </div>
      </div>

      {showPreview && (
        <div className="fixed inset-0 z-[9999] bg-black/90 overflow-y-auto">
            <div className="bg-white w-full max-w-[210mm] mx-auto p-4 flex justify-between items-center sticky top-0 shadow-2xl z-[10000] mt-4 rounded-t-xl border-b">
                <h3 className="font-bold text-gray-800">Print Preview (A4 Standard)</h3>
                <div className="flex gap-2">
                    <button onClick={handleDownloadPDFServer} disabled={isGeneratingPDF} className="bg-green-600 hover:bg-green-700 text-white px-5 py-2 rounded-lg flex items-center gap-2 font-bold shadow-sm transition active:scale-95">
                        {isGeneratingPDF ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"/> : <Download size={18}/>} Download PDF
                    </button>
                    <button onClick={handleNativePrint} className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-lg flex items-center gap-2 font-bold shadow-sm transition active:scale-95">
                        <Printer size={18}/> Print Fisik
                    </button>
                    <button onClick={() => setShowPreview(false)} className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-5 py-2 rounded-lg flex items-center gap-2 font-bold border transition">
                        <X size={18}/> Tutup
                    </button>
                </div>
            </div>
            <div id="printable-area" className="w-full flex flex-col items-center py-8">
                <div className="bg-white shadow-[0_0_50px_rgba(0,0,0,0.5)] w-[210mm] min-w-[210mm] max-w-[210mm] overflow-hidden flex flex-col items-stretch"> 
                    {bulkPrintData.map((group, index) => (
                        <React.Fragment key={index}>
                            <InvoiceTemplate data={group.items} isCancelled={group.isCancelled} />
                        </React.Fragment>
                    ))}
                </div>
            </div>
        </div>
      )}

      {isEditModalOpen && (
          <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4 backdrop-blur-sm">
              <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md animate-in zoom-in-95 max-h-[90vh] overflow-y-auto">
                  <div className="flex justify-between items-center mb-4">
                      <div>
                          <h3 className="font-black text-xl text-gray-900">Edit Invoice</h3>
                          <p className="text-xs font-mono text-gray-500 mt-1">{editInvoiceNumber} {currentRevision > 0 ? `· Rev.${currentRevision}` : ''}</p>
                      </div>
                      <button onClick={() => setIsEditModalOpen(false)} className="text-gray-400 hover:text-red-600 bg-gray-50 hover:bg-red-50 p-2 rounded-full transition"><X size={20}/></button>
                  </div>
                  <div className="flex gap-1 mb-5 bg-gray-100 p-1 rounded-xl">
                      <button onClick={() => setEditModalTab("pajak")} className={`flex-1 py-2 rounded-lg text-sm font-bold transition ${editModalTab === "pajak" ? "bg-white shadow text-gray-900" : "text-gray-500 hover:text-gray-700"}`}>Pajak & Biaya</button>
                      <button onClick={() => setEditModalTab("customer")} className={`flex-1 py-2 rounded-lg text-sm font-bold transition ${editModalTab === "customer" ? "bg-white shadow text-gray-900" : "text-gray-500 hover:text-gray-700"}`}>Data Customer</button>
                  </div>

                  {editModalTab === "pajak" && (
                  <div>
                      <div className="space-y-5">
                          <div className="grid grid-cols-3 gap-3">
                              <div>
                                  <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">VAT / PPN (%)</label>
                                  <input type="number" step="0.1" className="w-full p-2 border-2 border-gray-200 rounded-xl text-sm outline-none focus:border-green-500" value={editForm.vat_percent} onChange={(e) => setEditForm({...editForm, vat_percent: e.target.value})} />
                              </div>
                              <div>
                                  <label className="block text-[10px] font-bold text-blue-600 uppercase mb-1">VAT Nominal (IDR)</label>
                                  <input type="text" className="w-full p-2 border-2 border-blue-100 bg-blue-50 rounded-xl text-sm font-bold text-blue-700 outline-none focus:border-blue-500" value={formatInputDisplay(editForm.vat_amount)} onChange={(e) => setEditForm({...editForm, vat_amount: parseRawAmount(e.target.value)})} />
                              </div>
                              <div>
                                  <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">FSC (%)</label>
                                  <input type="number" step="0.1" className="w-full p-2 border-2 border-gray-200 rounded-xl text-sm outline-none focus:border-green-500" value={editForm.fsc_percent} onChange={(e) => setEditForm({...editForm, fsc_percent: e.target.value})} />
                              </div>
                          </div>
                          <div>
                          <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Invoice Date</label>
                          <input type="date" className="w-full p-2 border-2 border-gray-200 rounded-xl text-sm outline-none focus:border-green-500" value={editForm.invoice_date} onChange={(e) => setEditForm({...editForm, invoice_date: e.target.value})} />
                          <p className="text-[10px] text-gray-400 mt-1">Due date otomatis +30 hari (kecuali Cash)</p>
                      </div>
                      <div className="bg-yellow-50 p-4 rounded-xl text-[10px] text-yellow-800 border border-yellow-200 leading-relaxed">
                              <strong>Penting:</strong> VAT Nominal dihitung otomatis dari total semua resi. Ubah hanya jika ada selisih Rp 1 dengan Cortex.
                          </div>
                          <div onClick={() => setIsCash(!isCash)} className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition ${isCash ? 'bg-green-50 border-green-400' : 'bg-gray-50 border-gray-200 hover:border-gray-300'}`}>
                              <div className={`w-5 h-5 rounded flex items-center justify-center border-2 transition ${isCash ? 'bg-green-500 border-green-500' : 'border-gray-300'}`}>
                                  {isCash && <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
                              </div>
                              <div>
                                  <p className={`text-sm font-bold ${isCash ? 'text-green-800' : 'text-gray-600'}`}>Cash / Tunai</p>
                                  <p className={`text-[10px] ${isCash ? 'text-green-600' : 'text-gray-400'}`}>{isCash ? 'Due date = tanggal invoice' : 'Centang jika pembayaran tunai (tanpa tempo 30 hari)'}</p>
                              </div>
                          </div>
                      </div>
                      <div className="border-t pt-4 mt-4">
                          <div className="flex justify-between items-center mb-2">
                              <label className="text-xs font-bold text-gray-500 uppercase">Additional Charges (+)</label>
                              <button onClick={() => setAddCharges([...addCharges, { desc: '', amount: 0, taxable: true }])} className="text-[10px] bg-blue-50 text-blue-600 px-2 py-1 rounded-md border border-blue-200 font-bold hover:bg-blue-100">+ Tambah Biaya</button>
                          </div>
                          {addCharges.map((item, index) => (
                              <div key={index} className="flex flex-col gap-1 mb-2 p-2 border rounded-lg bg-gray-50">
                                  <div className="flex gap-2">
                                      <input placeholder="Contoh: Outer Packing" className="flex-grow p-2 text-xs border rounded-lg" value={item.desc} onChange={(e) => { const newArr = [...addCharges]; newArr[index].desc = e.target.value; setAddCharges(newArr); }} />
                                      <input type="text" placeholder="Nominal" className="w-32 p-2 text-xs border rounded-lg font-bold text-right" value={formatInputDisplay(item.amount)}
                                          onFocus={() => { if(item.amount === 0) { const newArr = [...addCharges]; newArr[index].amount = ""; setAddCharges(newArr); }}}
                                          onChange={(e) => { const val = parseRawAmount(e.target.value); const newArr = [...addCharges]; newArr[index].amount = val; setAddCharges(newArr); }} />
                                      <button onClick={() => setAddCharges(addCharges.filter((_, i) => i !== index))} className="text-red-400 hover:text-red-600 px-1"><X size={14}/></button>
                                  </div>
                                  <label className="flex items-center gap-1.5 text-[10px] text-gray-500 pl-1">
                                      <input type="checkbox" checked={item.taxable === false} onChange={(e) => { const newArr = [...addCharges]; newArr[index].taxable = !e.target.checked; setAddCharges(newArr); }} />
                                      Reimbursement (Non-Objek Pajak - tidak kena VAT, tetap masuk Grand Total)
                                  </label>
                              </div>
                          ))}
                      </div>
                      <div className="border-t pt-4 mt-4">
                          <div className="flex justify-between items-center mb-2">
                              <label className="text-xs font-bold text-gray-500 uppercase">Deductions (-)</label>
                              <button onClick={() => setDeductions([...deductions, { desc: '', amount: 0 }])} className="text-[10px] bg-red-50 text-red-600 px-2 py-1 rounded-md border border-red-200 font-bold hover:bg-red-100">+ Tambah Potongan</button>
                          </div>
                          {deductions.map((item, index) => (
                              <div key={index} className="flex gap-2 mb-2">
                                  <input placeholder="Contoh: Claim AWB..." className="flex-grow p-2 text-xs border rounded-lg" value={item.desc} onChange={(e) => { const newArr = [...deductions]; newArr[index].desc = e.target.value; setDeductions(newArr); }} />
                                  <input type="text" placeholder="Nominal" className="w-32 p-2 text-xs border rounded-lg font-bold text-right text-red-600" value={formatInputDisplay(item.amount)}
                                      onFocus={() => { if(item.amount === 0) { const newArr = [...deductions]; newArr[index].amount = ""; setDeductions(newArr); }}}
                                      onChange={(e) => { const val = parseRawAmount(e.target.value); const newArr = [...deductions]; newArr[index].amount = val; setDeductions(newArr); }} />
                                  <button onClick={() => setDeductions(deductions.filter((_, i) => i !== index))} className="text-red-400 hover:text-red-600 px-1"><X size={14}/></button>
                              </div>
                          ))}
                      </div>
                  </div>
                  )}

                  {editModalTab === "customer" && (
                  <div className="space-y-3">
                      <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-xs text-blue-800">
                          Perubahan data customer akan membuat <strong>Revisi baru</strong>. Semua AWB dalam invoice ini akan ikut berubah. Batas revisi: 5x.
                      </div>
                      <div>
                          <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Nama PT</label>
                          <input type="text" className="w-full p-2 border-2 border-gray-200 rounded-xl text-sm outline-none focus:border-blue-500" value={custForm.customer_name} onChange={e => setCustForm({...custForm, customer_name: e.target.value})} />
                      </div>
                      <div>
                          <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Customer Code</label>
                          <input type="text" className="w-full p-2 border-2 border-gray-200 rounded-xl text-sm outline-none focus:border-blue-500" value={custForm.customer_id} onChange={e => setCustForm({...custForm, customer_id: e.target.value})} />
                      </div>
                      <div>
                          <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Building / D/A</label>
                          <input type="text" className="w-full p-2 border-2 border-gray-200 rounded-xl text-sm outline-none focus:border-blue-500" value={custForm.building} onChange={e => setCustForm({...custForm, building: e.target.value})} />
                      </div>
                      <div>
                          <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Alamat 1</label>
                          <input type="text" className="w-full p-2 border-2 border-gray-200 rounded-xl text-sm outline-none focus:border-blue-500" value={custForm.address1} onChange={e => setCustForm({...custForm, address1: e.target.value})} />
                      </div>
                      <div>
                          <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Alamat 2</label>
                          <input type="text" className="w-full p-2 border-2 border-gray-200 rounded-xl text-sm outline-none focus:border-blue-500" value={custForm.address2} onChange={e => setCustForm({...custForm, address2: e.target.value})} />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                          <div>
                              <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Kota</label>
                              <input type="text" className="w-full p-2 border-2 border-gray-200 rounded-xl text-sm outline-none focus:border-blue-500" value={custForm.city} onChange={e => setCustForm({...custForm, city: e.target.value})} />
                          </div>
                          <div>
                              <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Kode Pos</label>
                              <input type="text" className="w-full p-2 border-2 border-gray-200 rounded-xl text-sm outline-none focus:border-blue-500" value={custForm.post_code} onChange={e => setCustForm({...custForm, post_code: e.target.value})} />
                          </div>
                      </div>
                      <div>
                          <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">NPWP</label>
                          <input type="text" className="w-full p-2 border-2 border-gray-200 rounded-xl text-sm outline-none focus:border-blue-500" value={custForm.npwp} onChange={e => setCustForm({...custForm, npwp: e.target.value})} />
                      </div>
                      <div>
                          <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Attn</label>
                          <input type="text" className="w-full p-2 border-2 border-gray-200 rounded-xl text-sm outline-none focus:border-blue-500" value={custForm.department} onChange={e => setCustForm({...custForm, department: e.target.value})} />
                      </div>
                  </div>
                  )}

                  <div className="mt-8 flex justify-end gap-3">
                      <button onClick={() => setIsEditModalOpen(false)} className="px-5 py-2.5 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl font-bold transition">Batal</button>
                      {editModalTab === "pajak" ? (
                          <button onClick={submitEdit} className="px-5 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-xl font-bold shadow-md transition">Simpan</button>
                      ) : (
                          <button onClick={submitRevision} disabled={currentRevision >= 5} className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-xl font-bold shadow-md transition">
                              {currentRevision >= 5 ? "Batas Revisi Tercapai" : `Buat Revisi (Rev.${(currentRevision||0)+1})`}
                          </button>
                      )}
                  </div>
              </div>
          </div>
      )}
      {isRevisionModalOpen && (
        <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md animate-in zoom-in-95 max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h3 className="font-black text-xl text-gray-900">History Revisi</h3>
                <p className="text-xs font-mono text-gray-500 mt-1">{revisionInvoiceNumber}</p>
              </div>
              <button onClick={() => setIsRevisionModalOpen(false)} className="text-gray-400 hover:text-red-600 bg-gray-50 hover:bg-red-50 p-2 rounded-full transition"><X size={20}/></button>
            </div>
            {loadingRevisions ? (
              <div className="text-center py-8 text-gray-400">Memuat...</div>
            ) : revisionHistory.length === 0 ? (
              <div className="text-center py-8 text-gray-400">Tidak ada history revisi</div>
            ) : (
              <div className="space-y-3">
                {[...revisionHistory].sort((a,b) => b.revision - a.revision).map((rev) => (
                  <div key={rev.revision} className={`rounded-xl border p-4 ${rev.revision_status === 'ACTIVE' ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-gray-50'}`}>
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-2">
                        <span className={`font-black text-lg ${rev.revision_status === 'ACTIVE' ? 'text-green-700' : 'text-gray-500'}`}>
                          {rev.revision === 0 ? 'Original' : `Rev.${rev.revision}`}
                        </span>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase ${rev.revision_status === 'ACTIVE' ? 'bg-green-200 text-green-800' : 'bg-gray-200 text-gray-600'}`}>
                          {rev.revision_status}
                        </span>
                      </div>
                      <span className="text-xs text-gray-500">{rev.awb_count} AWB</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="mt-4 text-xs text-gray-400 text-center">
              Total {revisionHistory.length} versi tersimpan
            </div>
          </div>
        </div>
      )}
      {isSplitModalOpen && (
        <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-lg animate-in zoom-in-95 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h3 className="font-black text-xl text-gray-900">Split Invoice</h3>
                <p className="text-xs font-mono text-gray-500 mt-1">{splitInvoiceNumber}</p>
              </div>
              <button onClick={() => setIsSplitModalOpen(false)} className="text-gray-400 hover:text-red-600 bg-gray-50 hover:bg-red-50 p-2 rounded-full transition"><X size={20}/></button>
            </div>
            <div className="mb-4">
              <label className="text-sm font-bold text-gray-700 mb-1 block">Tanggal Invoice Baru</label>
              <input type="date" value={splitDate} onChange={e => setSplitDate(e.target.value)}
                className="w-full border rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400" />
            </div>
            <div className="mb-4">
              <div className="flex justify-between items-center mb-2">
                <label className="text-sm font-bold text-gray-700">Pilih AWB yang dipindah ke invoice baru</label>
                <span className="text-xs text-gray-400">{splitSelectedIds.length}/{splitInvoiceItems.length} dipilih</span>
              </div>
              <div className="border rounded-xl overflow-hidden">
                {splitInvoiceItems.map((item, idx) => (
                  <div key={item.id} className={`flex items-center gap-3 px-4 py-3 ${idx % 2 === 0 ? 'bg-gray-50' : 'bg-white'}`}>
                    <input type="checkbox" checked={splitSelectedIds.includes(item.id)}
                      onChange={e => {
                        if (e.target.checked) setSplitSelectedIds(prev => [...prev, item.id]);
                        else setSplitSelectedIds(prev => prev.filter(x => x !== item.id));
                      }}
                      className="w-4 h-4 rounded accent-indigo-600" />
                    <div className="flex-1">
                      <div className="font-mono text-sm font-bold text-gray-800">{item.awb}</div>
                      <div className="text-xs text-gray-500">{item.consignee} · {item.weight_kg} kg · Rp {(item.base_price||0).toLocaleString('id-ID')}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {splitSelectedIds.length > 0 && splitSelectedIds.length < splitInvoiceItems.length && (
              <div className="bg-indigo-50 rounded-xl p-3 mb-4 text-xs text-indigo-700">
                <b>{splitSelectedIds.length} AWB</b> pindah ke invoice baru · <b>{splitInvoiceItems.length - splitSelectedIds.length} AWB</b> tersisa di {splitInvoiceNumber}
              </div>
            )}
            {splitSelectedIds.length === splitInvoiceItems.length && (
              <div className="bg-red-50 rounded-xl p-3 mb-4 text-xs text-red-700">
                ⚠️ Minimal 1 AWB harus tersisa di invoice asal
              </div>
            )}
            <div className="flex gap-2 justify-end">
              <button onClick={() => setIsSplitModalOpen(false)} className="px-4 py-2 rounded-xl border text-sm font-bold text-gray-600 hover:bg-gray-50">Batal</button>
              <button onClick={handleSplit} disabled={isSplitting || splitSelectedIds.length === 0 || splitSelectedIds.length === splitInvoiceItems.length || !splitDate}
                className="px-4 py-2 rounded-xl bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 disabled:opacity-50">
                {isSplitting ? "Memproses..." : "Split Invoice"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default InvoiceHistoryPage;